import configs from "./configs";

const PLACE_ART_MARKETPLACE = configs["PLACE_ART_MARKETPLACE"];
const PER_PAGE = 9;
const DEFAULT_PAGE = 1;

const PUBLIC_ART_API = configs["PUBLIC_ART_API"] + "/met";
const PLACE_ART_API = configs["PUBLIC_ART_API"] + "/placeart";
const PUBLIC_ART_BASE_API = configs["PUBLIC_ART_API"];

const formatEntries = artwork => {
  let urlPreview;

  if (artwork.primaryImageSmall) {
    urlPreview = artwork.primaryImageSmall;
  }

  return {
    attributions: {
      creator: {
        name: artwork.artistDisplayName ? artwork.artistDisplayName : ""
      }
    },
    collection: artwork.department,
    id: artwork.objectID,
    images: {
      preview: {
        url: urlPreview
      }
    },
    name: artwork.title,
    type: "collection",
    url: artwork.objectURL,
    ...artwork
  };
};

const formatResponse = ({ data, meta, filterItems, mediums }) => {
  const entries = data.map(artwork => {
    return {
      ...formatEntries(artwork)
    };
  });
  const { pagination } = meta;
  if (pagination.total > pagination.count + pagination.perPage) {
    meta.next_cursor = pagination.perPage * pagination.currentPage;
  } else {
    meta.next_cursor = null;
  }

  return { entries, meta, filterItems };
};

const fetchArtWorks = async ({ collection, cursor, artist, query }) => {
  let allArtworksUrl = PUBLIC_ART_API;
  let page = DEFAULT_PAGE;
  if (parseInt(cursor)) {
    page = (parseInt(cursor) + PER_PAGE) / PER_PAGE;
  }

  allArtworksUrl = `${allArtworksUrl}?page=${page}&perPage=${PER_PAGE}`;

  if (query) {
    allArtworksUrl += `&q=${query}`;
  }

  if (collection && collection != "") {
    allArtworksUrl += `&department=${collection}`;
  }

  const response = await fetch(allArtworksUrl);

  const intCursor = parseInt(cursor || 0);

  const { data, meta: apiMeta } = await response.json();

  const meta = {
    pagination: {
      count: intCursor,
      total: apiMeta?.total,
      perPage: PER_PAGE,
      currentPage: page
    }
  };
  return { data, meta, url: allArtworksUrl };
};

const fetchNgaArtWorks = async ({ collection, cursor, query }) => {
  let allArtworksUrl = PUBLIC_ART_BASE_API + "/nga";
  let page = DEFAULT_PAGE;
  if (parseInt(cursor)) {
    page = (parseInt(cursor) + PER_PAGE) / PER_PAGE;
  }

  allArtworksUrl = `${allArtworksUrl}?page=${page}&perPage=${PER_PAGE}`;

  if (query) {
    allArtworksUrl += `&q=${query}`;
  }

  if (collection && collection != "") {
    allArtworksUrl += `&department=${collection}`;
  }

  const response = await fetch(allArtworksUrl);

  const intCursor = parseInt(cursor || 0);

  const { data, meta: apiMeta } = await response.json();

  const meta = {
    pagination: {
      count: intCursor,
      total: apiMeta?.total,
      perPage: PER_PAGE,
      currentPage: page
    }
  };
  return { data, meta, url: allArtworksUrl };
};

const fetchCollections = async ({ category }) => {
  const deparmentsUrl = PUBLIC_ART_API + "/deparments";
  const response = await fetch(deparmentsUrl);
  const { data, meta } = await response.json();
  return data;
};

const fetchNgaCollections = async () => {
  const deparmentsUrl = PUBLIC_ART_BASE_API + "/nga/departments";
  const response = await fetch(deparmentsUrl);
  const { data, meta } = await response.json();
  return data;
};

const fetchArtists = async () => {
  const urlArtists = `${PLACE_ART_API}/authors?filter%5BhasActiveArtworks%5D=true&filter%5Bcategory%5D=artists`;

  const response = await fetch(urlArtists);
  const { data, meta } = await response.json();

  return data;
};

const fetchAuthors = async ({ category }) => {
  const url = `${PLACE_ART_API}/authors?filter%5Bcategory%5D=${category}`;
  const response = await fetch(url);
  const { data, meta } = await response.json();
  return data;
};

export const fetchMediums = async () => {
  const url = `${PLACE_ART_API}/mediums`;
  const response = await fetch(url);
  const { data, meta } = await response.json();
  return data;
};

export const fetchFrames = async () => {
  const url = `${PLACE_ART_API}/frames`;
  const response = await fetch(url);
  const { data, meta } = await response.json();
  return data;
};

const queryMet = async (dropdownFilter, cursor, query) => {
  const { data, meta, url } = await fetchArtWorks({ collection: dropdownFilter, cursor, query });

  const category = "museums";

  const filterItems = await fetchCollections({ category });

  const result = formatResponse({ data, meta, filterItems });
  return [url, result];
};

const queryArtists = async (dropdownFilter, cursor, query) => {
  const filterItems = await fetchArtists();

  const artist = dropdownFilter ? dropdownFilter : filterItems[0]?.id;

  if (!artist) {
    return [
      null,
      {
        filterItems: []
      }
    ];
  }

  const { data, meta, url } = await fetchArtWorks({ cursor, artist, query });

  const result = formatResponse({ data, meta, filterItems: filterItems });
  return [url, result];
};

const queryNga = async (dropdownFilter, cursor, query) => {
  const filterItems = await fetchNgaCollections();

  const collection = dropdownFilter ? dropdownFilter : filterItems[0]?.id;

  const { data, meta, url } = await fetchNgaArtWorks({ cursor, collection, query });

  const result = formatResponse({ data, meta, filterItems: filterItems });
  return [url, result];
};

export function queryPlaceArt(filter, dropdownFilter, cursor, query) {
  switch (filter) {
    case "placeart-artists":
      return queryArtists(dropdownFilter, cursor, query);
    case "met":
      return queryMet(dropdownFilter, cursor, query);
    default:
      return queryNga(dropdownFilter, cursor, query);
  }
}

export async function getTabs() {
  const availableTabs = ["collections", "met"];

  return availableTabs;
}

export async function getSignedUrl({ file, token, folder, apiUrl }) {
  const ACL = "public-read";
  const url = `${apiUrl}/get-upload-signed-url`;

  const folderName = folder.replace(/ /g, "_");

  const filenameParts = file.name.split(".");
  filenameParts[0] += `-${Date.now()}`;
  const filename = filenameParts.join(".").replace(/ /g, "_");

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
        authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        filename: filename,
        folder: folderName,
        content_type: file.type,
        ACL: ACL,
        size: file.size || 0
      })
    });

    if (!response.ok) {
      // If the response is not successful, throw an error
      const error = new Error(`Failed to get signed URL: ${response.status} ${response.statusText}`);
      error.status = response.status;
      throw error;
    }

    const json = await response.json();
    return json;
  } catch (error) {
    // Re-throw the error to propagate it to the parent function
    console.error("Failed to get signed URL: ", error);
    throw error;
  }
}
